import { FC, ReactNode, useRef, useState } from 'react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { closeModalSelectFiles, openModal } from '@store/modalsSlise'
import TYPES from '@modals/TYPES'
import { selectProfile } from '@store/profile.reducer'
import { AddFromPlatformMedia } from '@/utils/icons'
import { uploadImageToServer } from '@UI/Tiptap/utils/uploadImageToServer'
import Preloader from '@UI/Preloader'
import { Tooltip } from 'antd'
import cn from './index.module.css'

interface IFileField {
  label?: ReactNode
  value?: string
  accept?: string
  placeholder?: string
  isInvalid?: boolean
  onMediaSelect?: (file: MediaFileData | null) => void
  onFileSelect?: (file: File | null) => void
  showLocalUpload?: boolean
  showPlatformMedia?: boolean
  platformMediaType?: string
  uploadedFileNameText?: string
  galleryView?: boolean
  platformMediaTooltip?: string
  extraActions?: ReactNode
}

const FileField: FC<IFileField> = ({
  label,
  value,
  accept = 'image/png, image/gif, image/jpeg',
  placeholder,
  isInvalid,
  onMediaSelect,
  onFileSelect,
  showLocalUpload = true,
  showPlatformMedia,
  platformMediaType = 'Изображение',
  uploadedFileNameText = ' ',
  galleryView = false,
  extraActions,
  platformMediaTooltip = 'Выбрать из библиотеки',
}) => {
  //@ts-ignore
  const profile = useSelector(selectProfile)
  const dispatch = useDispatch()
  const [loadingFile, setLoadingFile] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canSelectFromPlatform = showPlatformMedia ?? Boolean(onMediaSelect)

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event?.target?.files?.[0] ?? null
    if (!file) return

    if (onFileSelect) {
      onFileSelect(file)
      event.target.value = ''
      return
    }

    if (!onMediaSelect) return

    setLoadingFile(true)
    const fileName = `Загружено ${uploadedFileNameText} ${dayjs().format('YYYY-MM-DD в HH:mm')}`

    try {
      const image = await uploadImageToServer({
        file,
        fileName,
        profileId: profile?.id,
      })

      onMediaSelect({ ...image, name: fileName })
      event.target.value = ''
    } finally {
      setLoadingFile(false)
    }
  }

  const openModalSelectFile = () => {
    if (!onMediaSelect) return

    dispatch(
      openModal({
        type: TYPES.SELECT_FILES_FROM_MEDIA_PLATFORM,
        data: {
          type: platformMediaType,
          onSuccess: (file: MediaFileData) => {
            onMediaSelect(file)
            dispatch(closeModalSelectFiles())
          },
          galleryView,
        },
      }),
    )
  }

  const openFileDialog = (event?: React.MouseEvent) => {
    event?.stopPropagation()
    event?.preventDefault()
    if (loadingFile || !showLocalUpload) return
    fileInputRef?.current?.click()
  }

  return (
    <div className={cn.wrapper}>
      {label && (
        <div className={cn.header}>
          <span className={classNames(cn.label, isInvalid && cn.labelInvalid)}>
            {label}
          </span>
        </div>
      )}
      <div
        className={classNames(cn.input, isInvalid && cn.invalid)}
        onClick={openFileDialog}
      >
        {loadingFile && (
          <div className={cn.preloader}>
            <Preloader />
          </div>
        )}
        {value ? (
          <div style={{ overflow: 'hidden' }}>
            <div className={cn.value}>{value}</div>
          </div>
        ) : (
          <span className={cn.placeholder}>{placeholder}</span>
        )}
        <div className={cn.actionContainer}>
          {showLocalUpload && (
            <button className={cn.iconButton} onClick={openFileDialog}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m8.56,17.9a0.5,0.5 0 0 1 0.5,0.5l0,2.5a1,1 0 0 0 1,1l12,0a1,1 0 0 0 1,-1l0,-2.5a0.5,0.5 0 0 1 1,0l0,2.5a2,2 0 0 1 -2,2l-12,0a2,2 0 0 1 -2,-2l0,-2.5a0.5,0.5 0 0 1 0.5,-0.5z"
                  fill="black"
                />
                <path
                  d="m15.71,9.15a0.5,0.5 0 0 1 0.7,0l3,3a0.5,0.5 0 0 1 -0.7,0.7l-2.15,-2.14l0,8.79a0.5,0.5 0 0 1 -1,0l0,-8.79l-2.15,2.14a0.5,0.5 0 1 1 -0.7,-0.7l3,-3z"
                  fill="black"
                />
              </svg>
            </button>
          )}
          {canSelectFromPlatform && (
            <Tooltip title={platformMediaTooltip}>
              <button
                aria-label={platformMediaTooltip}
                onClick={(event: React.MouseEvent) => {
                  event?.stopPropagation()
                  event?.preventDefault()
                  if (loadingFile) return
                  openModalSelectFile()
                }}
                className={cn.iconButton}
              >
                <AddFromPlatformMedia width={'20'} height={'20'} />
              </button>
            </Tooltip>
          )}
          {extraActions}
        </div>
      </div>
      {showLocalUpload && (
        <input
          type="file"
          accept={accept}
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      )}
    </div>
  )
}

export default FileField
